'use client';
import { useEffect } from 'react';
import Lenis from 'lenis';
import { SLIDE_COUNT, slideIndexById, slides } from '@/lib/presentation/slides';
import { goTo, next, prev, presentation } from '@/lib/presentation/store';
import { setHashNavigator, setLenis } from '@/components/experience/smoothScroll';

/** Accumulated wheel distance (px) that counts as one "next/previous" gesture. */
const WHEEL_THRESHOLD = 36;
/** A pause this long (ms) between wheel events starts a new gesture. */
const WHEEL_GAP = 170;
const SWIPE_THRESHOLD = 46;

const AFTER_ID = 'after-presentation';

function isEditable(el: Element | null) {
  if (!el) return false;
  const tag = el.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    (el as HTMLElement).isContentEditable
  );
}

/**
 * Presentation input: wheel, touch swipes, keyboard, hash links.
 *
 * While presenting, the document is locked (`html.v2-presenting`) and every gesture moves
 * exactly one slide. Trackpad inertia is filtered: after a slide change, a gesture only
 * counts once the wheel pauses or accelerates again, so one flick never skips two slides.
 * Moving past the last slide releases the lock and the page scrolls on to the FAQ and footer;
 * scrolling back to the top re-enters the presentation.
 */
export function useSlideInput(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    const root = document.documentElement;
    const reduced = presentation.get().reducedMotion;

    // Lenis smooths the released (document) part of the page only.
    const lenis = reduced ? null : new Lenis({ lerp: 0.1, smoothWheel: true });
    setLenis(lenis);
    let rafId = 0;
    const raf = (time: number) => {
      lenis?.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    if (lenis) rafId = requestAnimationFrame(raf);

    let armed = true;
    let acc = 0;
    let lastWheel = 0;
    let lastAbs = 0;

    const present = () => {
      root.classList.add('v2-presenting');
      lenis?.stop();
      window.scrollTo(0, 0);
      presentation.set({ mode: 'presenting' });
      armed = false;
    };

    const release = () => {
      root.classList.remove('v2-presenting');
      presentation.set({ mode: 'released' });
      lenis?.start();
      const after = document.getElementById(AFTER_ID);
      if (!after) return;
      if (lenis) lenis.scrollTo(after, { duration: 1.4 });
      else after.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth' });
    };

    const forward = () => {
      const { index } = presentation.get();
      if (index >= SLIDE_COUNT - 1) release();
      else next();
    };

    releaseHandle = release;

    // Start presenting (the page may have been restored mid-scroll).
    present();
    armed = true;

    const onWheel = (e: WheelEvent) => {
      const s = presentation.get();
      if (e.ctrlKey) return; // pinch-zoom
      if (s.mode === 'released') return;
      e.preventDefault();
      e.stopPropagation();

      const now = performance.now();
      const dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaMode === 2 ? e.deltaY * 400 : e.deltaY;
      const abs = Math.abs(dy);
      if (now - lastWheel > WHEEL_GAP || abs > lastAbs * 1.8 + 6) armed = true;
      lastWheel = now;
      lastAbs = abs;

      if (s.transitioning) {
        armed = false;
        acc = 0;
        return;
      }
      if (!armed) return;
      acc += dy;
      if (Math.abs(acc) >= WHEEL_THRESHOLD) {
        if (acc > 0) forward();
        else prev();
        acc = 0;
        armed = false;
      }
    };

    // Returning to the top of the released page re-enters the presentation (last slide).
    const onScroll = () => {
      if (presentation.get().mode === 'released' && window.scrollY <= 0) present();
    };

    let touchX = 0;
    let touchY = 0;
    const onTouchStart = (e: TouchEvent) => {
      touchX = e.touches[0].clientX;
      touchY = e.touches[0].clientY;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (presentation.get().mode === 'presenting' && e.cancelable) e.preventDefault();
    };
    const onTouchEnd = (e: TouchEvent) => {
      const s = presentation.get();
      if (s.mode !== 'presenting' || s.transitioning) return;
      const t = e.changedTouches[0];
      const dx = touchX - t.clientX;
      const dy = touchY - t.clientY;
      if (Math.abs(dy) < SWIPE_THRESHOLD || Math.abs(dy) < Math.abs(dx)) return;
      if (dy > 0) forward();
      else prev();
    };

    const onKey = (e: KeyboardEvent) => {
      const s = presentation.get();
      if (s.mode !== 'presenting' || e.metaKey || e.ctrlKey || e.altKey) return;
      const active = document.activeElement;
      if (isEditable(active)) return;
      // Space activates focused buttons/links; only hijack it on the page body.
      const onControl = active instanceof HTMLButtonElement || active instanceof HTMLAnchorElement;
      let handled = true;
      switch (e.key) {
        case 'ArrowDown':
        case 'PageDown':
          forward();
          break;
        case 'ArrowUp':
        case 'PageUp':
          prev();
          break;
        case ' ':
          if (onControl) handled = false;
          else if (e.shiftKey) prev();
          else forward();
          break;
        case 'Home':
          goTo(0);
          break;
        case 'End':
          goTo(SLIDE_COUNT - 1);
          break;
        default:
          handled = false;
      }
      if (handled) e.preventDefault();
    };

    // In-page links (/homepage#ecosystem, CTA "#movio-idea"…) fly to the matching slide.
    setHashNavigator((id) => {
      if (id === AFTER_ID || id === 'faq') return false;
      const index = id === 'main-content' ? 0 : slideIndexById[id];
      if (index === undefined) return false;
      if (presentation.get().mode === 'released') present();
      goTo(index);
      return true;
    });

    const onHashChange = () => {
      const id = decodeURIComponent(window.location.hash.slice(1));
      const index = slideIndexById[id];
      if (index !== undefined) {
        if (presentation.get().mode === 'released') present();
        goTo(index);
      }
    };

    // Deep link: start on the requested slide without a journey.
    const initial = slideIndexById[decodeURIComponent(window.location.hash.slice(1))];
    if (initial !== undefined && initial > 0) goTo(initial, { instant: true });

    // Keep the URL shareable without adding history entries per slide.
    let lastIndex = presentation.get().index;
    const unsubscribe = presentation.subscribe(() => {
      const { index } = presentation.get();
      if (index === lastIndex) return;
      lastIndex = index;
      const url = index === 0 ? window.location.pathname : `#${slides[index].id}`;
      window.history.replaceState(null, '', url);
    });

    window.addEventListener('wheel', onWheel, { passive: false, capture: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('keydown', onKey);
    window.addEventListener('hashchange', onHashChange);

    return () => {
      window.removeEventListener('wheel', onWheel, { capture: true });
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('hashchange', onHashChange);
      unsubscribe();
      setHashNavigator(null);
      releaseHandle = null;
      cancelAnimationFrame(rafId);
      lenis?.destroy();
      setLenis(null);
      root.classList.remove('v2-presenting');
    };
  }, [enabled]);
}

let releaseHandle: (() => void) | null = null;

/** Leaves the presentation for the FAQ / footer (used by the final-slide hint). */
export function releasePresentation() {
  releaseHandle?.();
}
